import {
	FormControl,
	FormControlLabel,
	FormHelperText,
	FormLabel,
	Radio,
	RadioGroup,
	Typography,
	TypographyTypeMap,
} from '@mui/material';
import { useId } from 'react';
import { FieldErrors, get, useController, useFormContext } from 'react-hook-form';
import { z } from 'zod';

type RadioValue = string | boolean | number;

export interface IRadioFieldProps {
	name: string;
	groupLabel?: string;
	groupLabelVariant?: TypographyTypeMap['props']['variant'];
	groupLabelColor?: TypographyTypeMap['props']['color'];
	required?: boolean;
	formHelperText?: string;
	horizontalInputs?: boolean;
	radioItems: {
		label: string;
		value: RadioValue;
	}[];
	dataTestId?: string;
}

type ValueOf<T> = T[keyof T];

export const radioFieldSchema = ((options) => {
	return {
		options,
		create: (fieldName: string, validation: ValueOf<typeof options>) => {
			return z.object({ [fieldName]: validation });
		},
	};
})({
	default: z.string().min(1, 'Required'),
	notRequired: z.string(),
});

export default function RadioField({
	groupLabel,
	groupLabelVariant = 'bodyMediumBook',
	groupLabelColor = 'text.secondary',
	name,
	horizontalInputs = false,
	radioItems,
	formHelperText,
	required = true,
	dataTestId,
}: IRadioFieldProps) {
	const { control } = useFormContext();
	const labelId = useId();

	const {
		field,
		formState: { errors },
	} = useController({ name, control });

	const error = get(errors, name) as FieldErrors;

	return (
		<FormControl fullWidth error={!!error} required={required}>
			{groupLabel && (
				<FormLabel id={labelId}>
					<Typography variant={groupLabelVariant} color={groupLabelColor}>
						{groupLabel}
					</Typography>
				</FormLabel>
			)}
			<RadioGroup
				row={horizontalInputs}
				value={field.value as RadioValue}
				onChange={field.onChange}
				aria-labelledby={labelId}
				data-test-id={dataTestId}
			>
				{radioItems.map((item) => (
					<FormControlLabel
						key={item.label}
						{...field}
						value={item.value}
						control={<Radio color="secondary" />}
						label={item.label}
						componentsProps={{
							typography: {
								variant: 'bodyMediumBook',
								color: 'text.secondary',
							},
						}}
					/>
				))}
			</RadioGroup>
			<FormHelperText>
				<>{error?.message ?? formHelperText}</>
			</FormHelperText>
		</FormControl>
	);
}
